import { useDispatch, useSelector } from 'react-redux'
import type { TypedUseSelectorHook } from 'react-redux'
import type { RootState, AppDispatch } from '../store/store'
import { useEffect, useRef, useCallback } from 'react'

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

/**
 * Hook for infinite scroll using IntersectionObserver.
 * @param callback - Function to call when the sentinel element becomes visible.
 * @param enabled - Whether the observer should be active (e.g., when there are more items to load).
 * @returns A ref to attach to a sentinel div at the bottom of the list.
 */
export function useInfiniteScroll(
  callback: () => void,
  enabled: boolean
) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(callback);

  // Keep the callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          callbackRef.current();
        }
      },
      { threshold: 0.1, rootMargin: '200px' }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [enabled]);

  return sentinelRef;
}
